import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import './UsernameSetup.css'

export default function UsernameSetup() {
  const { user, profile, fetchProfile } = useAuth()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [checking, setChecking] = useState(false)
  const [available, setAvailable] = useState(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const debounceRef = useRef(null)

  const isValid = /^[a-z0-9_]{3,20}$/.test(username)

  useEffect(() => {
    if (!username || !isValid) { setAvailable(null); return }
    setChecking(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .neq('id', user.id)
        .single()
      setAvailable(!data)
      setChecking(false)
    }, 500)
  }, [username])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!isValid) { setError('Username must be 3–20 characters, lowercase letters, numbers, or underscores only.'); return }
    if (!available) { setError('That username is taken.'); return }
    setSaving(true)
    setError('')
    const { error: err } = await supabase.from('profiles').update({
      username: username,
      username_set: true,
    }).eq('id', user.id)
    if (err) { setError(err.message); setSaving(false); return }
    await fetchProfile(user.id)
    navigate('/dashboard')
  }

  function handleChange(e) {
    const val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
    setUsername(val)
    setAvailable(null)
    setError('')
  }

  let statusMsg = null
  if (username.length > 0 && username.length < 3) statusMsg = { type: 'error', text: 'Too short — minimum 3 characters' }
  else if (username.length > 20) statusMsg = { type: 'error', text: 'Too long — maximum 20 characters' }
  else if (username && !isValid) statusMsg = { type: 'error', text: 'Only lowercase letters, numbers, and underscores' }
  else if (checking) statusMsg = { type: 'checking', text: 'Checking availability...' }
  else if (available === true) statusMsg = { type: 'available', text: `✓ @${username} is available!` }
  else if (available === false) statusMsg = { type: 'taken', text: `✕ @${username} is already taken` }

  return (
    <div className="setup-page page-fade">
      <div className="setup-card">
        <div className="setup-logo">
          <span className="logo-mark">H</span>
        </div>
        <h1>Choose your username</h1>
        <p className="setup-sub">This is your public identity on HumanOrigin. You can change it anytime from your profile settings.</p>

        <form onSubmit={handleSubmit}>
          <div className="setup-input-wrap">
            <span className="setup-at">@</span>
            <input
              type="text"
              value={username}
              onChange={handleChange}
              placeholder="yourname"
              maxLength={20}
              autoFocus
              autoComplete="off"
              autoCapitalize="none"
            />
          </div>

          {statusMsg && (
            <div className={`setup-status setup-status-${statusMsg.type}`}>
              {statusMsg.text}
            </div>
          )}

          <div className="setup-rules">
            <span className={username.length >= 3 ? 'rule-ok' : 'rule-dim'}>✓ 3–20 characters</span>
            <span className={/^[a-z0-9_]*$/.test(username) && username ? 'rule-ok' : 'rule-dim'}>✓ Letters, numbers, underscores</span>
            <span className={available === true ? 'rule-ok' : 'rule-dim'}>✓ Available</span>
          </div>

          <div className="setup-preview">
            humanorigin.app/u/<strong>{username || 'yourname'}</strong>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: 24 }}
            disabled={!isValid || !available || saving || checking}
          >
            {saving ? <span className="loading-spinner" /> : 'Confirm username →'}
          </button>
        </form>

        {error && <div className="error-msg" style={{ marginTop: 16 }}>{error}</div>}
      </div>
    </div>
  )
}
